/**
 * Client-side company tenant URL utilities (mirrors auth-service companyUrl.ts).
 */

export type AppEnvironment = "local" | "development" | "staging" | "production";

export const RESERVED_SUBDOMAINS = new Set([
  "company",
  "company-dev",
  "company-stg",
  "app",
  "admin",
  "admin-dev",
  "admin-stg",
  "www",
  "api",
  "auth",
  "chatbot",
  "ai-dev",
  "ai-stg",
  "socket",
  "mail",
  "beta",
]);

export const COMPANY_PORTAL_HOSTS = new Set(["beta", "company", "company-dev", "company-stg"]);

const BASE_DOMAIN = "criticalasset.com";

export function isReservedSubdomain(slug: string | null | undefined): boolean {
  if (!slug) return true;
  return RESERVED_SUBDOMAINS.has(slug.toLowerCase());
}

export function detectAppEnvironment(hostname?: string): AppEnvironment {
  const host = (hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")).toLowerCase();
  if (!host || host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost")) {
    return "local";
  }
  if (host.includes(".dev.") || host.startsWith("company-dev.") || host.includes("-dev.")) {
    return "development";
  }
  if (host.includes(".stg.") || host.startsWith("company-stg.") || host.includes("-stg.")) {
    return "staging";
  }
  return "production";
}

export function parseTenantSubdomain(hostname: string): string | null {
  const host = (hostname || "").toLowerCase().split(":")[0];
  if (!host || host === "localhost" || host === "127.0.0.1") {
    return null;
  }

  // Local dev: gems.localhost → gems (API still on localhost:4000 via config)
  if (host.endsWith(".localhost")) {
    const slug = host.split(".")[0];
    if (!slug || slug === "localhost") return null;
    if (COMPANY_PORTAL_HOSTS.has(slug) || isReservedSubdomain(slug)) return null;
    return slug;
  }

  const firstLabel = host.split(".")[0];
  if (COMPANY_PORTAL_HOSTS.has(firstLabel)) {
    return null;
  }

  const devMatch = host.match(/^([a-z0-9-]+)\.dev\.(.+)$/);
  if (devMatch) {
    const slug = devMatch[1];
    return isReservedSubdomain(slug) ? null : slug;
  }

  const stgMatch = host.match(/^([a-z0-9-]+)\.stg\.(.+)$/);
  if (stgMatch) {
    const slug = stgMatch[1];
    return isReservedSubdomain(slug) ? null : slug;
  }

  const betaMatch = host.match(/^([a-z0-9-]+)\.beta\.(.+)$/);
  if (betaMatch) {
    const slug = betaMatch[1];
    return isReservedSubdomain(slug) ? null : slug;
  }

  const parts = host.split(".");
  if (parts.length >= 3) {
    const slug = parts[0];
    if (isReservedSubdomain(slug)) return null;
    const domain = parts.slice(-2).join(".");
    if (domain === BASE_DOMAIN || host.endsWith(`.${BASE_DOMAIN}`)) {
      return slug;
    }
  }

  return null;
}

export function getTenantSubdomain(): string | null {
  if (typeof window === "undefined") return null;
  return parseTenantSubdomain(window.location.hostname);
}

export function isGenericPortal(): boolean {
  if (typeof window === "undefined") return true;
  return isGenericCompanyPortalHost(window.location.hostname);
}

/** Company self-signup is only offered on generic portal hosts, not tenant subdomains. */
export function isSignupAllowed(): boolean {
  return isGenericPortal();
}

export function isGenericCompanyPortalHost(hostname: string): boolean {
  const host = (hostname || "").toLowerCase().split(":")[0];
  if (!host || host === "localhost" || host === "127.0.0.1") return true;
  return COMPANY_PORTAL_HOSTS.has(host.split(".")[0]);
}

export function getAppEnvironment(): AppEnvironment {
  return detectAppEnvironment();
}

export interface BuildTenantUrlOptions {
  path?: string;
  env?: AppEnvironment;
  protocol?: string;
}

export function buildTenantUrl(subdomain: string, options: BuildTenantUrlOptions = {}): string {
  const slug = subdomain.toLowerCase().trim();
  const env = options.env ?? getAppEnvironment();
  const protocol = options.protocol ?? (env === "local" ? "http" : "https");

  let host: string;
  if (env === "local") {
    host = `${slug}.localhost:3005`;
  } else if (env === "development") {
    host = `${slug}.dev.${BASE_DOMAIN}`;
  } else if (env === "staging") {
    host = `${slug}.stg.${BASE_DOMAIN}`;
  } else {
    host = `${slug}.beta.${BASE_DOMAIN}`;
  }

  const path = options.path ? (options.path.startsWith("/") ? options.path : `/${options.path}`) : "";
  return `${protocol}://${host}${path}`;
}

/** Preview URL for signup helper text */
export function formatSubdomainPreview(subdomain: string): string {
  const env = getAppEnvironment();
  if (env === "development") return `${subdomain}.dev.${BASE_DOMAIN}`;
  if (env === "staging") return `${subdomain}.stg.${BASE_DOMAIN}`;
  if (env === "local") return `${subdomain}.localhost:3005`;
  return `${subdomain}.beta.${BASE_DOMAIN}`;
}

export function getAuthServiceBaseUrl(): string {
  if (typeof window === "undefined") return "";
  const env = getAppEnvironment();
  if (env === "local") {
    return `${window.location.protocol}//${window.location.hostname}:3001`;
  }
  return `${window.location.origin}`;
}
